import React from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Progress, Typography, Space } from 'antd';
import { UserOutlined, TeamOutlined, TagsOutlined, FileTextOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { adminQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import { apiService } from '../../../services/api';

const { Title } = Typography;

interface DashboardStats {
  totalMentees: number;
  totalMentors: number;
  totalCategories: number;
  totalJobPostings: number;
  activeMentees: number;
  activeMentors: number;
  newMenteesThisMonth: number;
  newMentorsThisMonth: number;
}

interface RecentUser {
  id: number;
  name: string;
  email: string;
  joinDate: string;
  status: string;
}

const defaultStats: DashboardStats = {
  totalMentees: 0,
  totalMentors: 0,
  totalCategories: 0,
  totalJobPostings: 0,
  activeMentees: 0,
  activeMentors: 0,
  newMenteesThisMonth: 0,
  newMentorsThisMonth: 0
};

const DashboardTab: React.FC = () => {
  const { data: stats = defaultStats, isLoading: statsLoading } = useQuery({
    queryKey: adminQueryKeys.stats,
    queryFn: async () => {
      const r = await apiService.getAdminStats();
      if (!r.success) throw new Error(r.message);
      return (r.data ?? defaultStats) as DashboardStats;
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: recentMentees = [], isLoading: menteesLoading } = useQuery({
    queryKey: adminQueryKeys.recentMentees(5),
    queryFn: async () => {
      const r = await apiService.getRecentMentees(5);
      if (!r.success) throw new Error(r.message);
      return (r.data ?? []) as RecentUser[];
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: recentMentors = [], isLoading: mentorsLoading } = useQuery({
    queryKey: adminQueryKeys.recentMentors(5),
    queryFn: async () => {
      const r = await apiService.getRecentMentors(5);
      if (!r.success) throw new Error(r.message);
      return (r.data ?? []) as RecentUser[];
    },
    staleTime: 1000 * 60 * 2,
  });

  const dashboardLoading = statsLoading || menteesLoading || mentorsLoading;

  const menteeColumns = [
    {
      title: 'Ad Soyad',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Katılım Tarihi',
      dataIndex: 'joinDate',
      key: 'joinDate',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    }
  ];

  const mentorColumns = [
    {
      title: 'Ad Soyad',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Katılım Tarihi',
      dataIndex: 'joinDate',
      key: 'joinDate',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    }
  ];

  return (
    <div className="panel-tab-content">
      <Title level={2} style={{ margin: 0, marginBottom: '24px', fontSize: '24px', fontWeight: 600 }}>Admin Dashboard</Title>
      <TabListLoading loading={dashboardLoading}>
      {/* İstatistik Kartları */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <Statistic
              title="Toplam Mentee"
              value={stats.totalMentees}
              prefix={<UserOutlined />}
              valueStyle={{ color: 'var(--primary-500)' }}
              suffix={
                <Space>
                  <span style={{ fontSize: '12px', color: 'var(--success)' }}>
                    <ArrowUpOutlined /> +{stats.newMenteesThisMonth}
                  </span>
                </Space>
              }
            />
            <Progress 
              percent={Math.round((stats.activeMentees / stats.totalMentees) * 100)} 
              size="small" 
              style={{ marginTop: '8px' }}
            />
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {stats.activeMentees} aktif mentee
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <Statistic
              title="Toplam Mentor"
              value={stats.totalMentors}
              prefix={<TeamOutlined />}
              valueStyle={{ color: 'var(--success)' }}
              suffix={
                <Space>
                  <span style={{ fontSize: '12px', color: 'var(--success)' }}>
                    <ArrowUpOutlined /> +{stats.newMentorsThisMonth}
                  </span>
                </Space>
              }
            />
            <Progress 
              percent={Math.round((stats.activeMentors / stats.totalMentors) * 100)} 
              size="small" 
              style={{ marginTop: '8px' }}
            />
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {stats.activeMentors} aktif mentor
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <Statistic
              title="Kategoriler"
              value={stats.totalCategories}
              prefix={<TagsOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              Aktif kategoriler
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <Statistic
              title="İş İlanları"
              value={stats.totalJobPostings}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: 'var(--accent-500)' }}
            />
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              Toplam iş ilanı
            </div>
          </Card>
        </Col>
      </Row>

      {/* Son Eklenenler */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Son Eklenen Mentee'ler" size="small" className="panel-table-card" style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <Table
              scroll={{ x: 'max-content' }}
              columns={menteeColumns}
              dataSource={recentMentees}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Son Eklenen Mentor'ler" size="small" className="panel-table-card" style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <Table
              scroll={{ x: 'max-content' }}
              columns={mentorColumns}
              dataSource={recentMentors}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
      </TabListLoading>
    </div>
  );
};

export default DashboardTab;
